import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Toaster } from './Toast.tsx'
import { TooltipProvider } from './Tooltip.tsx'

export type ThemePreference = 'system' | 'light' | 'dark'
export type Density = 'compact' | 'comfortable'

const THEME_KEY = 'rt.theme'
const DENSITY_KEY = 'rt.density'

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = window.localStorage.getItem(key)
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* private mode: preference lasts for this page only */
  }
}

/** Applies a theme preference to <html>. `system` removes the attribute so prefers-color-scheme decides. */
export function applyTheme(pref: ThemePreference) {
  const root = document.documentElement
  if (pref === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', pref)
}

export function applyDensity(d: Density) {
  document.documentElement.setAttribute('data-density', d)
}

/** Inline in index.html <head> to set theme/density before first paint (no flash). */
export const THEME_BOOT_SCRIPT = `try{var t=localStorage.getItem('${THEME_KEY}');if(t==='light'||t==='dark')document.documentElement.setAttribute('data-theme',t);var d=localStorage.getItem('${DENSITY_KEY}');document.documentElement.setAttribute('data-density',d==='comfortable'?'comfortable':'compact')}catch(e){}`

function subscribeScheme(cb: () => void) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}

function systemDark() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

interface UIContextValue {
  theme: ThemePreference
  resolvedTheme: 'light' | 'dark'
  setTheme: (t: ThemePreference) => void
  density: Density
  setDensity: (d: Density) => void
}

const UIContext = createContext<UIContextValue | null>(null)

/**
 * `forcedTheme` pins a site to one theme (the contributor app is light only): the stored preference is ignored and
 * `setTheme` does nothing. Pages that briefly take over the canvas (the robot-control cockpit) can still
 * `applyTheme` and restore `theme` after.
 */
export function UIProvider({ children, forcedTheme }: { children: ReactNode; forcedTheme?: 'light' | 'dark' }) {
  const [stored, setThemeState] = useState<ThemePreference>(() => read(THEME_KEY, ['system', 'light', 'dark'], 'system'))
  const theme: ThemePreference = forcedTheme ?? stored
  const [density, setDensityState] = useState<Density>(() => read(DENSITY_KEY, ['compact', 'comfortable'], 'compact'))
  const dark = useSyncExternalStore(subscribeScheme, systemDark, () => false)
  useEffect(() => applyTheme(theme), [theme])
  useEffect(() => applyDensity(density), [density])
  // Focus rings are for keyboard users. Some widgets (Radix tabs, menus) move focus from script after a click, which
  // the browser then shows as :focus-visible; remembering how the last interaction happened lets CSS hide those.
  useEffect(() => {
    const root = document.documentElement
    const pointer = () => { root.dataset.input = 'pointer' }
    const keyboard = (e: KeyboardEvent) => { if (!e.metaKey && !e.ctrlKey && !e.altKey) root.dataset.input = 'keyboard' }
    window.addEventListener('pointerdown', pointer, true)
    window.addEventListener('keydown', keyboard, true)
    return () => {
      window.removeEventListener('pointerdown', pointer, true)
      window.removeEventListener('keydown', keyboard, true)
    }
  }, [])
  const setTheme = useCallback(
    (t: ThemePreference) => {
      if (forcedTheme) return
      write(THEME_KEY, t)
      setThemeState(t)
    },
    [forcedTheme],
  )
  const setDensity = useCallback((d: Density) => {
    write(DENSITY_KEY, d)
    setDensityState(d)
  }, [])
  const value = useMemo<UIContextValue>(
    () => ({ theme, resolvedTheme: theme === 'system' ? (dark ? 'dark' : 'light') : theme, setTheme, density, setDensity }),
    [theme, dark, setTheme, density, setDensity],
  )
  return (
    <UIContext.Provider value={value}>
      <TooltipProvider delayDuration={450} skipDelayDuration={200}>
        {children}
        <Toaster />
      </TooltipProvider>
    </UIContext.Provider>
  )
}

const FALLBACK: UIContextValue = {
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => {},
  density: 'compact',
  setDensity: () => {},
}

export function useUI(): UIContextValue {
  return useContext(UIContext) ?? FALLBACK
}

export function useDensity(): Density {
  return useUI().density
}
